import { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, ArrowRight, CheckCircle2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { CalendlyBooking } from "../components/CalendlyBooking";
import { usePageMeta } from "../hooks/usePageMeta";
import { CONTACT_DETAILS, CINEMATIC } from "../data/site";
import { useContent } from "../context/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCES = [
  "Tenant or Leaseholder",
  "Property Investor",
  "Refurbishment / Development",
  "Other Enquiry",
];

const inputCls =
  "w-full bg-cream-3 border border-forest/20 px-4 py-3.5 text-sm text-ink placeholder:text-sage/60 transition-colors duration-300 focus:border-gold focus:outline-none";

const Field = ({ label, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className="block text-[11px] tracking-[0.2em] uppercase font-bold text-forest mb-2.5">
      {label}
    </label>
    {children}
  </div>
);

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", audience: AUDIENCES[0], message: "" });
  const [status, setStatus] = useState("idle");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/enquiries`, form);
      setStatus("done");
      toast.success("Thank you — your enquiry has been received.");
    } catch (err) {
      setStatus("idle");
      toast.error("Sorry, your enquiry could not be sent. Please try again.");
    }
  };

  if (status === "done") {
    return (
      <div data-testid="contact-success-panel" className="bg-cream-3 border border-gold/50 p-10 sm:p-14 text-center">
        <CheckCircle2 size={44} strokeWidth={1.2} className="text-gold-dark mx-auto" />
        <h2 className="font-serif text-3xl font-medium text-forest mt-6">Thank You, {form.name.split(" ")[0]}.</h2>
        <p className="text-sm leading-[1.8] text-sage max-w-md mx-auto mt-4">
          Your enquiry has been received. We&rsquo;ll be in touch shortly — usually within one
          working day — to arrange your consultation.
        </p>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={submit} className="bg-cream-3 border border-forest/10 p-8 sm:p-12 space-y-7">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
        <Field label="Full Name" htmlFor="contact-name">
          <input
            id="contact-name"
            data-testid="contact-name-input"
            required
            value={form.name}
            onChange={set("name")}
            placeholder="Your name"
            className={inputCls}
          />
        </Field>
        <Field label="Email Address" htmlFor="contact-email">
          <input
            id="contact-email"
            data-testid="contact-email-input"
            type="email"
            required
            value={form.email}
            onChange={set("email")}
            placeholder="you@example.com"
            className={inputCls}
          />
        </Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
        <Field label="Phone (Optional)" htmlFor="contact-phone">
          <input
            id="contact-phone"
            data-testid="contact-phone-input"
            type="tel"
            value={form.phone}
            onChange={set("phone")}
            placeholder="+44 ..."
            className={inputCls}
          />
        </Field>
        <Field label="I Am Enquiring As" htmlFor="contact-audience">
          <select
            id="contact-audience"
            data-testid="contact-audience-select"
            value={form.audience}
            onChange={set("audience")}
            className={inputCls}
          >
            {AUDIENCES.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Your Message" htmlFor="contact-message">
        <textarea
          id="contact-message"
          data-testid="contact-message-input"
          required
          rows={6}
          value={form.message}
          onChange={set("message")}
          placeholder="Tell us a little about your situation or goals..."
          className={`${inputCls} resize-y`}
        />
      </Field>
      <button
        type="submit"
        data-testid="contact-submit-button"
        disabled={status === "sending"}
        className="btn-gold w-full sm:w-auto justify-center disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {status === "sending" ? "Sending..." : "Send Enquiry"} <ArrowRight size={14} strokeWidth={2.2} />
      </button>
      <p className="text-[11.5px] leading-relaxed text-sage/80">
        Your details are used only to respond to your enquiry. We never share them with third
        parties without your agreement.
      </p>
    </form>
  );
};

const Contact = () => {
  usePageMeta(
    "Contact Elin's Consultancy | Property Advice",
    "Book a consultation with Elin's Consultancy — independent tenant and leasehold advice and property investment support."
  );
  const content = useContent();
  const det = { ...CONTACT_DETAILS, ...(content?.contact || {}) };
  const contactRows = [
    { icon: Mail, label: "Email", value: det.email, href: `mailto:${det.email}`, testid: "contact-email-link" },
    { icon: Phone, label: "Telephone", value: det.phone, href: `tel:${det.phone.replace(/[^+\d]/g, "")}`, testid: "contact-phone-link" },
    ...(det.address ? [{ icon: Building2, label: "Address", value: det.address, testid: "contact-address" }] : []),
    { icon: MapPin, label: "Service Area", value: det.area, testid: "contact-area" },
  ];
  return (
    <Layout>
      <PageHero
        eyebrow="Contact"
        lines={[
          "Let’s Discuss Your",
          <>
            <em className="italic font-normal text-gold-soft">Property Goals</em>
          </>,
        ]}
        text="Whether you need tenant or leasehold advice, are looking to invest, or want support with a refurbishment project — send us a message and we'll arrange an initial consultation."
        image={CINEMATIC.grandHall}
        imageAlt="A grand hall interior with classical columns and a sweeping staircase"
      />

      <section data-testid="contact-section" className="bg-cream">
        <div className="wrap-x py-20 sm:py-24 grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-14">
          <Reveal>
            <p className="eyebrow text-gold-dark">Get in Touch</p>
            <h2 className="font-serif text-4xl font-medium text-forest leading-[1.1] mt-5 mb-8">
              We&rsquo;d Love to<br />Hear From You
            </h2>
            <div className="space-y-6">
              {contactRows.map((c) => (
                <div key={c.label} className="flex items-start gap-5">
                  <span className="w-12 h-12 shrink-0 rounded-full border border-gold flex items-center justify-center">
                    <c.icon size={20} strokeWidth={1.4} className="text-gold-dark" />
                  </span>
                  <div>
                    <p className="text-[10.5px] tracking-[0.24em] uppercase font-bold text-sage">{c.label}</p>
                    {c.href ? (
                      <a
                        href={c.href}
                        data-testid={c.testid}
                        className="inline-flex items-center min-h-[44px] -my-2 text-[15px] text-forest font-medium mt-1 hover:text-gold-dark transition-colors duration-300"
                      >
                        {c.value}
                      </a>
                    ) : (
                      <p data-testid={c.testid} className="text-[15px] text-forest font-medium mt-1">{c.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="w-full h-px bg-forest/15 my-10" />
            <p className="text-[13px] leading-[1.8] text-sage max-w-sm">
              Consultations are available by phone, video call or in person by arrangement. We aim
              to respond to every enquiry within one working day.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <ContactForm />
          </Reveal>
        </div>
      </section>

      <CalendlyBooking url={det.calendly_url} />
    </Layout>
  );
};

export default Contact;
